import type { ReactNode } from 'react'
import { XIcon } from 'lucide-react'
import { cn } from '../../lib/utils'

export type NotificationCardVariant = 'success' | 'warning' | 'info' | 'default'

export interface NotificationCardAction {
	label:    string
	onClick?: () => void
	href?:    string
}

export interface NotificationCardProps {
	icon:        ReactNode
	title:       ReactNode
	body?:       ReactNode
	dateChip?:   ReactNode
	actions?:    NotificationCardAction[]
	variant?:    NotificationCardVariant
	unread?:     boolean
	onClose?:    () => void
	className?:  string
}

const ICON_WRAP: Record<NotificationCardVariant, string> = {
	success: 'bg-(--color-success-light) text-(--color-success-dark)',
	warning: 'bg-(--color-destructive-light) text-(--color-destructive)',
	info:    'bg-(--color-info-light) text-(--color-info-dark)',
	default: 'bg-(--color-muted) text-(--color-muted-foreground)',
}

const ACTION_TONE: Record<NotificationCardVariant, string> = {
	success: 'text-(--color-brand) hover:opacity-80',
	warning: 'text-(--color-destructive) hover:opacity-80',
	info:    'text-(--color-brand) hover:opacity-80',
	default: 'text-(--color-brand) hover:opacity-80',
}

export function NotificationCard({
	icon, title, body, dateChip, actions, variant = 'default',
	unread, onClose, className,
}: NotificationCardProps) {
	return (
		<div className={cn(
			'relative flex items-start gap-3 rounded-(--radius-md) bg-(--color-card) px-3 py-3 shadow-xs',
			className,
		)}>
			<div className={cn(
				'flex h-9 w-9 shrink-0 items-center justify-center rounded-(--radius-sm)',
				ICON_WRAP[variant],
			)}>
				{icon}
			</div>

			<div className="flex min-w-0 flex-1 flex-col gap-1">
				<div className="text-sm font-[500] text-(--color-foreground)">{title}</div>
				{body && (
					<div className="text-sm text-(--color-muted-foreground)">{body}</div>
				)}

				{dateChip && (
					<div className="mt-1">
						{dateChip}
					</div>
				)}

				{actions && actions.length > 0 && (
					<div className="mt-1 flex items-center gap-3">
						{actions.map((a, i) => (
							a.href ? (
								<a
									key={i}
									href={a.href}
									className={cn('text-sm font-[500]', ACTION_TONE[variant])}
								>
									{a.label}
								</a>
							) : (
								<button
									key={i}
									type="button"
									onClick={a.onClick}
									className={cn('text-sm font-[500]', ACTION_TONE[variant])}
								>
									{a.label}
								</button>
							)
						))}
					</div>
				)}
			</div>

			{unread && (
				<span className="absolute right-3 top-3 h-2 w-2 rounded-full bg-(--color-destructive)" />
			)}
			{onClose && (
				<button
					type="button"
					onClick={onClose}
					aria-label="Close"
					className="ml-1 -mr-1 -mt-1 rounded p-1 text-(--color-muted-foreground) hover:text-(--color-foreground)"
				>
					<XIcon className="size-4" />
				</button>
			)}
		</div>
	)
}
